/**
 * BudgetFormDialog
 *
 * Modal de criação / edição de orçamento, extraído de Budgets.tsx para que possa
 * ser usado em qualquer lugar da aplicação sem abrir uma nova aba.
 *
 * Props:
 *   open            – controla visibilidade
 *   onClose         – chamado quando o usuário fecha ou cancela
 *   onSaved?        – chamado após salvar com sucesso (para recarregar lista na página pai)
 *   initialPatient? – pré-seleciona um paciente (objeto com ao menos id + full_name)
 *   editingBudgetId?– quando fornecido, carrega o orçamento e abre em modo edição
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, Grid, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Divider, Autocomplete, FormControlLabel, Switch, Tabs, Tab, Chip,
  InputAdornment, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as BudgetIcon, Person as PersonIcon,
  MedicalServices as MedicalIcon, AttachMoney as MoneyIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

/* ─── helpers ───────────────────────────────────────────────────── */
const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const emptyItem = {
  procedure_id: '', procedure_name: '', procedure_code: '', procedure_description: '',
  category_name: '', tooth_number: '', tooth_face: '', tooth_region: '',
  quantity: 1, unit_price: 0, discount_percentage: 0, notes: '',
  estimated_duration_minutes: 0, estimated_sessions: 1, requires_anesthesia: false,
  materials: [], sort_order: 0, group_name: '',
};

const defaultBudgetForm = () => ({
  discount_type: 'percentage',
  discount_value: 0,
  additional_charges: 0,
  payment_method: '',
  payment_conditions: '',
  installments: 1,
  entry_value: 0,
  validity_days: 30,
  clinical_indication: '',
  diagnosis: '',
  diagnosis_code: '',
  notes: '',
  internal_notes: '',
  dental_arch: '',
  show_unit_prices: true,
  show_procedure_codes: true,
  show_tooth_info: true,
  show_professional_info: true,
  show_payment_conditions: true,
  show_terms: true,
  show_warranty: false,
});

const defaultPatientData = () => ({
  full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '',
  birth_date: '', gender: '',
  address_street: '', address_number: '', address_city: '', address_state: '', address_zip: '',
});

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

/* ─── props ──────────────────────────────────────────────────────── */
export interface BudgetFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** Paciente já selecionado ao abrir o diálogo (criação nova). */
  initialPatient?: any;
  /** Quando fornecido, carrega e edita esse orçamento existente. */
  editingBudgetId?: string | null;
}

/* ─── component ──────────────────────────────────────────────────── */
const BudgetFormDialog: React.FC<BudgetFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  initialPatient,
  editingBudgetId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* form state */
  const [tabValue, setTabValue] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  /* patient */
  const [createPatient, setCreatePatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [patientData, setPatientData] = useState(defaultPatientData());

  /* professional */
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);

  /* procedures catalog search */
  const [procedureSearch, setProcedureSearch] = useState('');
  const [procedureOptions, setProcedureOptions] = useState<any[]>([]);

  /* budget form */
  const [budgetForm, setBudgetForm] = useState(defaultBudgetForm());
  const [items, setItems] = useState<any[]>([{ ...emptyItem }]);

  /* AI */
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiDescription, setAiDescription] = useState('');
  const [aiError, setAiError] = useState('');

  /* track last-loaded editing id to avoid re-fetch */
  const loadedEditingRef = useRef<string | null>(null);
  /* track last-applied initialPatient.id to avoid re-apply */
  const lastInitialPatientId = useRef<string | null>(null);

  /* ─── reset ──────────────────────────────────────────────────── */
  const resetForm = useCallback(() => {
    setCreatePatient(false);
    setSelectedPatient(null);
    setSelectedProfessional(null);
    setPatientSearch('');
    setPatientOptions([]);
    setProfessionalSearch('');
    setProcedureSearch('');
    setProcedureOptions([]);
    setPatientData(defaultPatientData());
    setBudgetForm(defaultBudgetForm());
    setItems([{ ...emptyItem }]);
    setEditingId(null);
    setTabValue(0);
    loadedEditingRef.current = null;
  }, []);

  /* ─── on open / initialPatient ───────────────────────────────── */
  useEffect(() => {
    if (!open) return;

    // editing mode
    if (editingBudgetId && editingBudgetId !== loadedEditingRef.current) {
      loadedEditingRef.current = editingBudgetId;
      lastInitialPatientId.current = null;
      (async () => {
        try {
          const res = await axios.get(getApiUrl(`/clinic/budgets/${editingBudgetId}`));
          const b = res.data?.data;
          if (!b) return;
          setEditingId(b.id);
          if (b.patient) { setSelectedPatient(b.patient); setCreatePatient(false); }
          if (b.professional) setSelectedProfessional(b.professional);
          setBudgetForm({
            discount_type: b.discount_type || 'percentage',
            discount_value: b.discount_value || 0,
            additional_charges: b.additional_charges || 0,
            payment_method: b.payment_method || '',
            payment_conditions: b.payment_conditions || '',
            installments: b.installments || 1,
            entry_value: b.entry_value || 0,
            validity_days: 30,
            clinical_indication: b.clinical_indication || '',
            diagnosis: b.diagnosis || '',
            diagnosis_code: b.diagnosis_code || '',
            notes: b.notes || '',
            internal_notes: b.internal_notes || '',
            dental_arch: b.dental_arch || '',
            show_unit_prices: b.show_unit_prices !== false,
            show_procedure_codes: b.show_procedure_codes !== false,
            show_tooth_info: b.show_tooth_info !== false,
            show_professional_info: b.show_professional_info !== false,
            show_payment_conditions: b.show_payment_conditions !== false,
            show_terms: b.show_terms !== false,
            show_warranty: b.show_warranty || false,
          });
          setItems(b.items?.length > 0 ? b.items : [{ ...emptyItem }]);
          setTabValue(0);
        } catch (e) { console.error('Error loading budget for edit', e); }
      })();
      return;
    }

    // creation with pre-filled patient
    if (!editingBudgetId && initialPatient?.id && lastInitialPatientId.current !== initialPatient.id) {
      lastInitialPatientId.current = initialPatient.id;
      resetForm();
      setSelectedPatient(initialPatient);
      setPatientOptions([initialPatient]);
      setTabValue(0);
    }
  }, [open, editingBudgetId, initialPatient, resetForm]);

  /* on close: reset flags so next open re-applies */
  useEffect(() => {
    if (!open) {
      loadedEditingRef.current = null;
      lastInitialPatientId.current = null;
    }
  }, [open]);

  /* ─── keep selectedPatient in options ───────────────────────── */
  useEffect(() => {
    if (!open || createPatient || !selectedPatient?.id) return;
    setPatientOptions(opts => {
      if (opts.some((x: any) => x.id === selectedPatient.id)) return opts;
      return [selectedPatient, ...opts];
    });
  }, [selectedPatient, open, createPatient]);

  /* ─── initial patient list when dialog opens ─────────────────── */
  useEffect(() => {
    if (!open || createPatient) return;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/patients'), { params: { search: '' } });
        if (res.data?.success !== false) {
          const list: any[] = res.data?.data || [];
          setPatientOptions(prev => {
            const ids = new Set(list.map((x: any) => x.id));
            const extras = prev.filter((x: any) => x?.id && !ids.has(x.id));
            return [...extras, ...list];
          });
        }
      } catch { /* silent */ }
    })();
  }, [open, createPatient]);

  /* ─── patient search ─────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/patients'), { params: { search: patientSearch || '' } });
        if (res.data?.success !== false) {
          const list: any[] = res.data?.data || [];
          setPatientOptions(prev => {
            const ids = new Set(list.map((x: any) => x.id));
            const extras = prev.filter((x: any) => x?.id && !ids.has(x.id));
            return [...extras, ...list];
          });
        } else setPatientOptions([]);
      } catch { setPatientOptions([]); }
    }, patientSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [patientSearch, open]);

  /* ─── professional search ────────────────────────────────────── */
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), { params: { search: professionalSearch } });
        setProfessionalOptions(res.data?.data || []);
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [professionalSearch]);

  /* ─── initial procedure list (when on tab 1) ─────────────────── */
  useEffect(() => {
    if (!open || tabValue !== 1) return;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/procedures'), { params: { search: '' } });
        if (res.data?.success !== false) setProcedureOptions(res.data?.data || []);
      } catch { /* silent */ }
    })();
  }, [open, tabValue]);

  /* ─── procedure search ───────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/procedures'), { params: { search: procedureSearch || '' } });
        if (res.data?.success !== false) setProcedureOptions(res.data?.data || []);
        else setProcedureOptions([]);
      } catch { setProcedureOptions([]); }
    }, procedureSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [procedureSearch, open]);

  /* ─── totals ─────────────────────────────────────────────────── */
  const calculateItemTotal = (item: any) => {
    const sub = (item.quantity || 1) * (item.unit_price || 0);
    return sub - sub * ((item.discount_percentage || 0) / 100);
  };
  const subtotal = items.reduce((s, i) => s + calculateItemTotal(i), 0);
  const discountAmount = budgetForm.discount_type === 'percentage'
    ? subtotal * (budgetForm.discount_value / 100)
    : budgetForm.discount_value;
  const total = subtotal - discountAmount + budgetForm.additional_charges;
  const installmentValue = budgetForm.installments > 0 ? (total - budgetForm.entry_value) / budgetForm.installments : total;

  /* ─── item helpers ───────────────────────────────────────────── */
  const addItem = () => setItems(prev => [...prev, { ...emptyItem, sort_order: prev.length }]);
  const removeItem = (i: number) => { if (items.length > 1) setItems(prev => prev.filter((_, idx) => idx !== i)); };
  const updateItem = (i: number, field: string, value: any) =>
    setItems(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });
  const selectProcedureForItem = (i: number, proc: any) => {
    if (!proc) return;
    setItems(prev => {
      const u = [...prev];
      u[i] = {
        ...u[i],
        procedure_id: proc.id,
        procedure_name: proc.name,
        procedure_code: proc.code || '',
        procedure_description: proc.description || '',
        category_name: proc.category?.name || '',
        unit_price: proc.price || 0,
        estimated_duration_minutes: proc.duration_minutes || 0,
        requires_anesthesia: proc.requires_anesthesia || false,
      };
      return u;
    });
  };

  /* ─── save ───────────────────────────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: any = {
        ...budgetForm,
        professional_id: selectedProfessional?.id || null,
        items: items.filter(i => i.procedure_name),
      };
      if (createPatient) {
        payload.create_patient = true;
        payload.patient_data = patientData;
        payload.patient_name = patientData.full_name;
      } else if (selectedPatient) {
        payload.patient_id = selectedPatient.id;
        payload.patient_name = selectedPatient.full_name;
      }
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/budgets/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Orçamento atualizado com sucesso!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/budgets'), payload);
        setSnackbar({ open: true, message: 'Orçamento criado com sucesso!', severity: 'success' });
      }
      onSaved?.();
      resetForm();
      onClose();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar orçamento', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /* ─── AI ─────────────────────────────────────────────────────── */
  const parseAiBudget = (text: string) => {
    const result: { items: any[]; clinical_indication: string; diagnosis: string; notes: string; payment_conditions: string } = {
      items: [], clinical_indication: '', diagnosis: '', notes: '', payment_conditions: '',
    };
    const lines = text.split('\n').map((l: string) => l.trim()).filter(Boolean);
    let cur: any = null; let cN = false; let cP = false;
    for (const line of lines) {
      const lo = line.toLowerCase();
      if (lo.startsWith('indicação clínica:') || lo.startsWith('indicacao clinica:') || lo.startsWith('indicação:')) { result.clinical_indication = line.split(':').slice(1).join(':').trim(); cN = false; cP = false; continue; }
      if (lo.startsWith('diagnóstico:') || lo.startsWith('diagnostico:')) { result.diagnosis = line.split(':').slice(1).join(':').trim(); cN = false; cP = false; continue; }
      if (lo.startsWith('observaç') || lo.startsWith('observac') || lo.startsWith('nota:') || lo.startsWith('notas:')) { result.notes = line.split(':').slice(1).join(':').trim(); cN = true; cP = false; continue; }
      if (lo.startsWith('condições de pagamento:') || lo.startsWith('condicoes de pagamento:') || lo.startsWith('pagamento:') || lo.startsWith('forma de pagamento:')) { result.payment_conditions = line.split(':').slice(1).join(':').trim(); cP = true; cN = false; continue; }
      if (cN && !lo.match(/^(procedimento|item|\d+[\.\)\-]|nome:|valor:|preço:|categoria:|quantidade:|desconto:|dente:|face:|observ)/)) { result.notes += ' ' + line; continue; }
      if (cP && !lo.match(/^(procedimento|item|\d+[\.\)\-]|nome:|valor:|preço:|categoria:|quantidade:|desconto:|dente:|face:|observ)/)) { result.payment_conditions += ' ' + line; continue; }
      cN = false; cP = false;
      const isNew = lo.startsWith('procedimento:') || lo.startsWith('item:') || lo.startsWith('nome:') || lo.startsWith('serviço:') || lo.startsWith('servico:') || /^\d+[\.\)\-]\s/.test(line);
      if (isNew) { if (cur) result.items.push(cur); cur = { ...emptyItem, procedure_name: line.replace(/^\d+[\.\)\-]\s*/, '').replace(/^(procedimento|item|nome|serviço|servico):\s*/i, '').trim() }; continue; }
      if (cur) {
        if (lo.startsWith('valor:') || lo.startsWith('preço:') || lo.startsWith('preco:') || lo.startsWith('valor unitário:')) { const v = parseFloat(line.split(':').slice(1).join(':').trim().replace(/[rR]\$\s?/g, '').replace(/\./g, '').replace(',', '.')); if (!isNaN(v)) cur.unit_price = v; }
        else if (lo.startsWith('categoria:') || lo.startsWith('grupo:')) { cur.category_name = line.split(':').slice(1).join(':').trim(); cur.group_name = cur.category_name; }
        else if (lo.startsWith('quantidade:') || lo.startsWith('qtd:') || lo.startsWith('sessões:') || lo.startsWith('sessoes:')) { const q = parseInt(line.split(':').slice(1).join(':').trim()); if (!isNaN(q) && q > 0) cur.quantity = q; }
        else if (lo.startsWith('desconto:') || lo.startsWith('desc:')) { const d = parseFloat(line.split(':').slice(1).join(':').trim().replace('%', '')); if (!isNaN(d)) cur.discount_percentage = d; }
        else if (lo.startsWith('dente:') || lo.startsWith('região:') || lo.startsWith('elemento:')) { cur.tooth_number = line.split(':').slice(1).join(':').trim(); }
        else if (lo.startsWith('face:')) { cur.tooth_face = line.split(':').slice(1).join(':').trim(); }
        else if (lo.startsWith('código:') || lo.startsWith('codigo:') || lo.startsWith('cód:') || lo.startsWith('cod:')) { cur.procedure_code = line.split(':').slice(1).join(':').trim(); }
        else if (lo.startsWith('descrição:') || lo.startsWith('descricao:') || lo.startsWith('detalhe:')) { cur.notes = line.split(':').slice(1).join(':').trim(); }
        else if (lo.startsWith('duração:') || lo.startsWith('duracao:') || lo.startsWith('tempo:')) { const d = parseInt(line.split(':').slice(1).join(':').trim()); if (!isNaN(d)) cur.estimated_duration_minutes = d; }
      }
    }
    if (cur) result.items.push(cur);
    if (result.items.length === 0) result.items = [{ ...emptyItem, procedure_name: '', notes: text }];
    return result;
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiDescription.trim()) { setAiError('Preencha o nome do paciente e a descrição dos procedimentos'); return; }
    setAiGenerating(true); setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/budgets/ai-generate'), { patient_name: aiPatientName.trim(), description: aiDescription.trim() });
      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');
      const raw = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.budget || JSON.stringify(aiData));
      const parsed = parseAiBudget(raw);
      resetForm();
      const m = patientOptions.find((p: any) => p.full_name?.toLowerCase().includes(aiPatientName.toLowerCase()));
      if (m) setSelectedPatient(m);
      if (parsed.items.length > 0) setItems(parsed.items);
      setBudgetForm(prev => ({ ...prev, clinical_indication: parsed.clinical_indication || '', diagnosis: parsed.diagnosis || '', notes: parsed.notes || '', payment_conditions: parsed.payment_conditions || '' }));
      setAiDialogOpen(false);
      setTabValue(1);
      setSnackbar({ open: true, message: 'Orçamento gerado com IA! Revise antes de salvar.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar orçamento com IA');
    } finally { setAiGenerating(false); }
  };

  /* ─── close helper ───────────────────────────────────────────── */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /* ─── render ─────────────────────────────────────────────────── */
  return (
    <>
      {/* ────── CREATE / EDIT DIALOG ────── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, maxHeight: '100%', borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }
            : { borderRadius: 3, maxHeight: '95vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2, flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <BudgetIcon sx={{ color: '#14b8a6', flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingId ? 'Editar Orçamento' : 'Novo Orçamento'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              startIcon={<AiIcon />}
              onClick={() => setAiDialogOpen(true)}
              sx={{ color: '#8b5cf6', textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'flex' } }}
            >
              Gerar com IA
            </Button>
            <IconButton onClick={handleClose} aria-label="Fechar"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflow: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              minHeight: { xs: 44, sm: 48 },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: { xs: 'auto', sm: 120 } },
              '& .MuiTabs-indicator': { bgcolor: '#14b8a6' },
            }}
          >
            <Tab label="Paciente" icon={<PersonIcon />} iconPosition="start" />
            <Tab label="Procedimentos" icon={<MedicalIcon />} iconPosition="start" />
            <Tab label="Financeiro" icon={<MoneyIcon />} iconPosition="start" />
            <Tab label="Inf. Clínicas" icon={<BudgetIcon />} iconPosition="start" />
            <Tab label="Configurações" />
          </Tabs>

          {/* TAB 0 – Paciente */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={createPatient} onChange={(e) => setCreatePatient(e.target.checked)} color="primary" />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Criar novo paciente junto com o orçamento</Typography>}
              />
            </Box>

            {!createPatient ? (
              <Box>
                <Autocomplete
                  options={patientOptions}
                  getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - CPF: ${o.cpf}` : ''}${o.phone ? ` - Tel: ${o.phone}` : ''}`}
                  isOptionEqualToValue={(a: any, b: any) => !!a?.id && a.id === b?.id}
                  value={selectedPatient}
                  onChange={(_, v) => setSelectedPatient(v)}
                  onInputChange={(_, v) => setPatientSearch(v)}
                  onOpen={() => { if (patientOptions.length === 0) setPatientSearch(''); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => <TextField {...params} label="Buscar paciente existente" placeholder="Digite nome, CPF ou telefone..." fullWidth />}
                  noOptionsText={patientSearch ? 'Nenhum paciente encontrado' : 'Digite para buscar pacientes...'}
                  sx={{ mb: 2 }}
                />
                {selectedPatient && (
                  <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Paciente Selecionado:</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><Typography variant="body2"><strong>Nome:</strong> {selectedPatient.full_name}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2"><strong>CPF:</strong> {selectedPatient.cpf || '-'}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2"><strong>Telefone:</strong> {selectedPatient.phone || '-'}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2"><strong>Email:</strong> {selectedPatient.email || '-'}</Typography></Grid>
                    </Grid>
                  </Card>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Nome Completo *" value={patientData.full_name} onChange={(e) => setPatientData({ ...patientData, full_name: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="CPF" value={patientData.cpf} onChange={(e) => setPatientData({ ...patientData, cpf: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth><InputLabel>Gênero</InputLabel>
                    <Select value={patientData.gender} label="Gênero" onChange={(e) => setPatientData({ ...patientData, gender: e.target.value })}>
                      <MenuItem value="male">Masculino</MenuItem><MenuItem value="female">Feminino</MenuItem><MenuItem value="other">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Telefone" value={patientData.phone} onChange={(e) => setPatientData({ ...patientData, phone: e.target.value })} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="WhatsApp" value={patientData.whatsapp} onChange={(e) => setPatientData({ ...patientData, whatsapp: e.target.value })} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Email" value={patientData.email} onChange={(e) => setPatientData({ ...patientData, email: e.target.value })} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Data de Nascimento" type="date" InputLabelProps={{ shrink: true }} value={patientData.birth_date} onChange={(e) => setPatientData({ ...patientData, birth_date: e.target.value })} /></Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Endereço" size="small" /></Divider></Grid>
                <Grid item xs={12} md={5}><TextField fullWidth label="Rua" size="small" value={patientData.address_street} onChange={(e) => setPatientData({ ...patientData, address_street: e.target.value })} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Número" size="small" value={patientData.address_number} onChange={(e) => setPatientData({ ...patientData, address_number: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Cidade" size="small" value={patientData.address_city} onChange={(e) => setPatientData({ ...patientData, address_city: e.target.value })} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Estado" size="small" value={patientData.address_state} onChange={(e) => setPatientData({ ...patientData, address_state: e.target.value })} /></Grid>
              </Grid>
            )}

            <Divider sx={{ my: 3 }}><Chip label="Profissional Responsável" size="small" /></Divider>
            <Autocomplete
              options={professionalOptions}
              getOptionLabel={(o: any) => `${o.full_name} - ${o.registration_type || 'CRM'} ${o.registration_number || ''} ${o.registration_state || ''} ${o.specialty ? `(${o.specialty})` : ''}`}
              value={selectedProfessional}
              onChange={(_, v) => setSelectedProfessional(v)}
              onInputChange={(_, v) => setProfessionalSearch(v)}
              renderInput={(params) => <TextField {...params} label="Profissional responsável" placeholder="Buscar por nome ou registro..." fullWidth />}
              noOptionsText="Nenhum profissional encontrado"
            />
          </TabPanel>

          {/* TAB 1 – Procedimentos */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Itens do Orçamento</Typography>
              <Button startIcon={<AddItemIcon />} onClick={addItem} sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Adicionar Item</Button>
            </Box>

            {items.map((item, idx) => (
              <Card key={idx} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: 2, position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d9488' }}>Item {idx + 1}</Typography>
                  {items.length > 1 && <IconButton size="small" onClick={() => removeItem(idx)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={procedureOptions}
                      getOptionLabel={(o: any) => `${o.name}${o.code ? ` (${o.code})` : ''} - ${formatCurrency(o.price)}`}
                      onChange={(_, v) => selectProcedureForItem(idx, v)}
                      onInputChange={(_, v) => setProcedureSearch(v)}
                      onOpen={() => { if (procedureOptions.length === 0) setProcedureSearch(''); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => <TextField {...params} label="Buscar procedimento cadastrado" placeholder="Digite para buscar..." size="small" fullWidth />}
                      noOptionsText={procedureSearch ? 'Nenhum procedimento encontrado' : 'Digite para buscar procedimentos...'}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Nome do Procedimento *" value={item.procedure_name} onChange={(e) => updateItem(idx, 'procedure_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="Código" value={item.procedure_code} onChange={(e) => updateItem(idx, 'procedure_code', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="Categoria" value={item.category_name} onChange={(e) => updateItem(idx, 'category_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={1.5}>
                    <TextField fullWidth size="small" label="Dente" value={item.tooth_number} placeholder="Ex: 11" onChange={(e) => updateItem(idx, 'tooth_number', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={1.5}>
                    <TextField fullWidth size="small" label="Face" value={item.tooth_face} placeholder="Ex: MOD" onChange={(e) => updateItem(idx, 'tooth_face', e.target.value)} />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField fullWidth size="small" label="Qtd" type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4} md={3}>
                    <TextField fullWidth size="small" label="Preço Unitário (R$)" type="number" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} inputProps={{ min: 0, step: 0.01 }} />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField fullWidth size="small" label="Desc. (%)" type="number" value={item.discount_percentage} onChange={(e) => updateItem(idx, 'discount_percentage', Number(e.target.value))} inputProps={{ min: 0, max: 100 }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Grupo" value={item.group_name} placeholder="Ex: Prótese" onChange={(e) => updateItem(idx, 'group_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box sx={{ textAlign: 'right', pt: 1 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>Total do item:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(calculateItemTotal(item))}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Observações do item" value={item.notes} onChange={(e) => updateItem(idx, 'notes', e.target.value)} multiline rows={1} />
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 2, mt: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>{items.filter(i => i.procedure_name).length} item(ns) no orçamento</Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>Subtotal: {formatCurrency(subtotal)}</Typography>
                  {discountAmount > 0 && <Typography variant="body2" sx={{ color: '#ef4444' }}>Desconto: -{formatCurrency(discountAmount)}</Typography>}
                  {budgetForm.additional_charges > 0 && <Typography variant="body2" sx={{ color: '#6b7280' }}>Acréscimo: +{formatCurrency(budgetForm.additional_charges)}</Typography>}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0d9488' }}>Total: {formatCurrency(total)}</Typography>
                </Grid>
              </Grid>
            </Card>
          </TabPanel>

          {/* TAB 2 – Financeiro */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Desconto Global</Typography></Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Tipo de Desconto</InputLabel>
                  <Select value={budgetForm.discount_type} label="Tipo de Desconto" onChange={(e) => setBudgetForm({ ...budgetForm, discount_type: e.target.value })}>
                    <MenuItem value="percentage">Percentual (%)</MenuItem>
                    <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label={budgetForm.discount_type === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'} type="number" value={budgetForm.discount_value} onChange={(e) => setBudgetForm({ ...budgetForm, discount_value: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Acréscimo (R$)" type="number" value={budgetForm.additional_charges} onChange={(e) => setBudgetForm({ ...budgetForm, additional_charges: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Validade (dias)" type="number" value={budgetForm.validity_days} onChange={(e) => setBudgetForm({ ...budgetForm, validity_days: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Condições de Pagamento" size="small" /></Divider></Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Forma de Pagamento</InputLabel>
                  <Select value={budgetForm.payment_method} label="Forma de Pagamento" onChange={(e) => setBudgetForm({ ...budgetForm, payment_method: e.target.value })}>
                    <MenuItem value="">Não definido</MenuItem>
                    {['Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 'PIX', 'Boleto', 'Transferência', 'Convênio'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Parcelas" type="number" value={budgetForm.installments} onChange={(e) => setBudgetForm({ ...budgetForm, installments: Number(e.target.value) })} inputProps={{ min: 1 }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Entrada (R$)" type="number" value={budgetForm.entry_value} onChange={(e) => setBudgetForm({ ...budgetForm, entry_value: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ pt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Valor da parcela:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(installmentValue)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Condições de pagamento (texto livre)" value={budgetForm.payment_conditions} onChange={(e) => setBudgetForm({ ...budgetForm, payment_conditions: e.target.value })} multiline rows={2} placeholder="Ex: Entrada de R$ 500,00 + 10x de R$ 200,00 no cartão" />
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography variant="body2">Subtotal:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(subtotal)}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="body2">Desconto:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(discountAmount)}</Typography></Grid>
                    {budgetForm.additional_charges > 0 && <>
                      <Grid item xs={6}><Typography variant="body2">Acréscimo:</Typography></Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600 }}>+{formatCurrency(budgetForm.additional_charges)}</Typography></Grid>
                    </>}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={6}><Typography variant="h6" sx={{ fontWeight: 700 }}>Total:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(total)}</Typography></Grid>
                    {budgetForm.entry_value > 0 && <>
                      <Grid item xs={6}><Typography variant="body2">Entrada:</Typography></Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(budgetForm.entry_value)}</Typography></Grid>
                    </>}
                    {budgetForm.installments > 1 && <>
                      <Grid item xs={6}><Typography variant="body2">{budgetForm.installments}x de:</Typography></Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(installmentValue)}</Typography></Grid>
                    </>}
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 3 – Informações Clínicas */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Indicação Clínica" value={budgetForm.clinical_indication} onChange={(e) => setBudgetForm({ ...budgetForm, clinical_indication: e.target.value })} multiline rows={2} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Diagnóstico" value={budgetForm.diagnosis} onChange={(e) => setBudgetForm({ ...budgetForm, diagnosis: e.target.value })} multiline rows={2} /></Grid>
              <Grid item xs={6} md={3}><TextField fullWidth label="CID-10" value={budgetForm.diagnosis_code} onChange={(e) => setBudgetForm({ ...budgetForm, diagnosis_code: e.target.value })} size="small" /></Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Arcada Dentária</InputLabel>
                  <Select value={budgetForm.dental_arch} label="Arcada Dentária" onChange={(e) => setBudgetForm({ ...budgetForm, dental_arch: e.target.value })}>
                    <MenuItem value="">N/A</MenuItem>
                    <MenuItem value="superior">Superior</MenuItem>
                    <MenuItem value="inferior">Inferior</MenuItem>
                    <MenuItem value="both">Ambas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth label="Observações (visíveis no orçamento impresso)" value={budgetForm.notes} onChange={(e) => setBudgetForm({ ...budgetForm, notes: e.target.value })} multiline rows={3} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Notas Internas (NÃO aparecem no impresso)" value={budgetForm.internal_notes} onChange={(e) => setBudgetForm({ ...budgetForm, internal_notes: e.target.value })} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fef3c7' } }} /></Grid>
            </Grid>
          </TabPanel>

          {/* TAB 4 – Configurações */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Configurações de Impressão</Typography>
            <Grid container spacing={1}>
              {[
                { key: 'show_unit_prices', label: 'Mostrar preços unitários' },
                { key: 'show_procedure_codes', label: 'Mostrar códigos dos procedimentos' },
                { key: 'show_tooth_info', label: 'Mostrar informações dentárias (dente/face)' },
                { key: 'show_professional_info', label: 'Mostrar informações do profissional' },
                { key: 'show_payment_conditions', label: 'Mostrar condições de pagamento' },
                { key: 'show_terms', label: 'Mostrar termos e condições' },
                { key: 'show_warranty', label: 'Mostrar garantia' },
              ].map((opt) => (
                <Grid item xs={12} md={6} key={opt.key}>
                  <FormControlLabel
                    control={<Switch checked={(budgetForm as any)[opt.key]} onChange={(e) => setBudgetForm({ ...budgetForm, [opt.key]: e.target.checked })} color="primary" />}
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, borderTop: '1px solid #e5e7eb', gap: 1, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button fullWidth={isMobile} onClick={handleClose} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600, minWidth: { sm: 160 } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : editingId ? 'Salvar Alterações' : 'Criar Orçamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────── AI DIALOG ────── */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => !aiGenerating && setAiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, m: isMobile ? 0 : 2, maxHeight: isMobile ? '100%' : undefined } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Orçamento com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} disabled={aiGenerating}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              Descreva os procedimentos e valores desejados de forma livre. A IA irá organizar tudo em um orçamento profissional. Revise sempre antes de salvar.
            </Alert>
            {aiError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{aiError}</Alert>}
          </Box>
          <TextField
            fullWidth label="Nome do Paciente *" placeholder="Ex: Maria Silva"
            value={aiPatientName} onChange={(e) => setAiPatientName(e.target.value)}
            disabled={aiGenerating} sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth label="Descreva os procedimentos e valores *"
            placeholder={'Ex: Consulta R$ 300, peeling R$ 800, 3 sessões laser R$ 500 cada'}
            value={aiDescription} onChange={(e) => setAiDescription(e.target.value)}
            disabled={aiGenerating} multiline rows={6}
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><BudgetIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <Typography variant="caption" sx={{ color: '#9ca3af', mt: 1, display: 'block' }}>
            Dica: quanto mais detalhes você incluir, mais completo será o orçamento gerado.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPatientName.trim() || !aiDescription.trim()}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600, minWidth: 200 }}
          >
            {aiGenerating ? 'Gerando orçamento...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────── Snackbar ────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BudgetFormDialog;
